<template>
  <div>
    <AddCustomMachineModal
      :machine-data-form="customMachineForm"
      :machine-data="machineData"
      :show="showAddCustomMachineModal"
      :available-processes="processesByTech"
      mode="'add'"
      @close="handleCloseAddModal"
      @add="addMachine"
      @tech-changed="fetchProcessesByTech"
    />
    <EditCustomMachineModal
      :machine-data-form="customMachineForm"
      :machine-data="machineData"
      :show="showEditCustomMachineModal"
      :available-processes="processesByTech"
      mode="'edit'"
      @close="showEditCustomMachineModal = false"
      @edit="editMachine"
      @tech-changed="fetchProcessesByTech"
    />
    <ViewMachineModal
      :machine-data="machineData"
      :filled-form-data="filledFormData"
      :show="showViewMachineModal"
      @close="showViewMachineModal = false"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import useCopyObject from '@/composables/copyObject.js';
import { transformObjectToArray } from '@/helpers';
import { addCustomMachineForm, populateMachineFormValues } from '@/utils/machineform';
import AddCustomMachineModal from '@/views/Library/machine/modals/AddCustomMachineModal.vue';
import EditCustomMachineModal from '@/views/Library/machine/modals/EditCustomMachineModal.vue';
import ViewMachineModal from '@/views/Library/machine/modals/ViewMachineModal.vue';

export default {
  name: 'CustomMachine',

  components: { AddCustomMachineModal, EditCustomMachineModal, ViewMachineModal },

  props: {
    machineData: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
  },

  emits: ['get-machines', 'highlight-machine', 'add-to-inhouse', 'reset-machine-data'],

  data() {
    return {
      showAddCustomMachineModal: false,
      showEditCustomMachineModal: false,
      showViewMachineModal: false,
      customMachineForm: this.getInitialFormState(),
      filledFormData: {},
      processesByTech: {},
    };
  },

  computed: {
    ...mapState('application', ['axiosInstance']),
    ...mapState(['user', 'organization']),

    updatedProcessSynonyms() {
      return this.organization.process_synonyms;
    },

    ...mapState({
      currentTechs: 'curTechs',
    }),

    updatedCurTechs() {
      const currentTechnology = transformObjectToArray(this.currentTechs);
      return currentTechnology;
    },

    updatedProcesses() {
      let actualProcesses = useCopyObject(this.updatedProcessSynonyms || {});

      const updatedProcesses = Object.values(actualProcesses || {}).map(prc => ({
        uid: prc.uid,
        label: prc.prc_name || prc.prc_acronym,
        value: prc.uid,
      }));

      return updatedProcesses;
    },

    updatedFilteredRestrictions() {
      const userRole = this.user.user_role;
      if (userRole === 'user') {
        return [{ label: 'Owner', value: 'owner' }];
      } else {
        return [
          { label: 'Owner', value: 'owner' },
          { label: 'My Organization', value: 'organization' },
        ];
      }
    },
  },

  watch: {
    machineData: {
      handler(machine) {
        if (this.type === 'add') {
          this.showAddCustomMachineModal = true;
        } else if (this.type === 'edit') {
          this.mode = 'edit';
          this.showEditCustomMachineModal = true;
        } else if (this.type === 'view' && Object.keys(machine).length) {
          this.createViewDataTable();
        }
      },
      deep: true,
    },

    'machineData.tech': {
      handler(newValue) {
        if (!newValue) return;

        this.fetchProcessesByTech(newValue);
        this.populateCustomMachineForm();

        this.$nextTick(() => {
          this.$forceUpdate();
        });
      },
      deep: true,
    },
  },

  mounted() {
    if (this.machineData && Object.keys(this.machineData).length > 0) {
      this.populateCustomMachineForm();
    }
  },

  methods: {
    getInitialFormState() {
      return JSON.parse(JSON.stringify(addCustomMachineForm));
    },

    createViewDataTable() {
      this.filledFormData = {};
      this.filledFormData = populateMachineFormValues(this.machineData);
      this.showViewMachineModal = true;
    },

    addMachine(formData) {
      if (formData.mac_id) {
        delete formData.mac_id;
      }

      this.axiosInstance
        .post('/api/v1/machine/', formData)
        .then(response => {
          this.$root.notify('success', 'Custom Machine ' + response.data.mac_name + ' created', 3000);
          this.$emit('highlight-machine', response.data.mac_id);
          this.$emit('get-machines');
          this.$emit('add-to-inhouse', response.data);
        })
        .catch(error => {
          this.$root.notify('error', error.response.data.error_message, 3000);
        });
    },

    editMachine(formData) {
      this.axiosInstance
        .put(`/api/v1/machine/${formData.mac_id}/`, formData)
        .then(response => {
          this.$root.notify('success', 'Machine ' + response.data.mac_name + ' edited', 3000);
          this.$emit('get-machines');
        })
        .catch(error => {
          this.$root.notify('error', error.response.data.error_message, 3000);
        });
    },

    fetchProcessesByTech(tech) {
      if (!tech) return;

      this.axiosInstance
        .get(`/api/v1/prc-acronyms/?tech=${tech}`)
        .then(response => {
          if (!response.data) return;

          const processes = response.data.map(prc => ({
            uid: prc.uid,
            label: prc.prc_name || prc.prc_acronym,
            value: prc.uid,
          }));

          this.processesByTech = {
            ...this.processesByTech,
            [tech]: processes,
          };

          this.$forceUpdate();
        })
        .catch(error => {
          console.error('Error fetching processes by technology:', error);
        });
    },

    populateCustomMachineForm() {
      if (!this.machineData || !Object.keys(this.machineData).length) {
        return;
      }

      this.customMachineForm = this.getInitialFormState();

      Object.values(this.customMachineForm).forEach(section => {
        if (!Array.isArray(section)) return;

        section.forEach(field => {
          if (!field || !field.db_name) return;

          if (field.db_name === 'tech') {
            field.options = this.updatedCurTechs;
          } else if (field.db_name === 'prc_uid') {
            const selectedTech = this.customMachineForm?.General?.find(f => f.db_name === 'tech')?.value;

            if (selectedTech && this.processesByTech[selectedTech]) {
              field.options = this.processesByTech[selectedTech];
            } else {
              field.options = this.updatedProcesses;
            }
          } else if (field.db_name === 'restriction') {
            field.options = this.updatedFilteredRestrictions;
          }
        });
      });
    },

    handleCloseAddModal() {
      this.showAddCustomMachineModal = false;
      this.$emit('reset-machine-data');
    },
  },
};
</script>
